import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { Dropdown } from 'react-bootstrap'
import Select from 'react-select'
import { toast } from 'react-toastify'

import Pagination, { ItemPerPage } from 'pages/parts/Pagination'
import { PGET, GET, POST } from 'helpers/api'
import { checkRole, formatMoney, formatDateWithTime } from 'helpers/helpers'
import DidoxLoginModal from './DidoxLoginModal'
import CreateUpdateModal from '../documentsIn/CreateUpdateModal'

// Didox incoming-invoice inbox: sync incoming ESF documents, then import a selected
// one into the existing documents_in/create form (pre-filled, not auto-created).
function Index() {
	const { t } = useTranslation()

	const reduxAccount = useSelector(state => state.account)

	const LIST_URL = '/services/web/api/didox/incoming-invoices'

	const canImport = checkRole('POS_DOCUMENT_IN_ADD')

	const statuses = [
		{ id: null, name: t('ALL') },
		{ id: 1, name: t('SIGNED') },
		{ id: 0, name: t('WAITING') },
	]

	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [ItemsPerPage, setItemsPerPage] = useState(20)
	const [pageCount, setPageCount] = useState(0)
	const [poses, setPoses] = useState([])
	const [data, setData] = useState([])

	const today = new Date().toISOString().slice(0, 10)
	const monthAgo = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10)

	const [filterData, setFilterData] = useState({
		posId: reduxAccount.posId,
		status: null,
		search: '',
		page: 0,
		random: 0,
	})

	// Didox sync controls
	const [token, setToken] = useState(sessionStorage.getItem('didoxToken') || '')
	const [syncFrom, setSyncFrom] = useState(monthAgo)
	const [syncTo, setSyncTo] = useState(today)
	const [syncing, setSyncing] = useState(false)
	const [syncProgress, setSyncProgress] = useState(null)
	const [showLogin, setShowLogin] = useState(false)

	// Reception (qabul qilish) modal — opened in-place when importing into documents_in
	const [showReception, setShowReception] = useState(false)
	const [prefillData, setPrefillData] = useState(null)

	async function getData() {
		const response = await PGET(LIST_URL, { ...filterData, size: ItemsPerPage }, { loader: true })
		if (response) {
			setPageCount(Math.ceil(response.headers['x-total-count'] / ItemsPerPage))
			setData(response.data)
			setDropdownIsOpen(false)
		}
	}

	async function paginate(page) {
		const response = await PGET(LIST_URL, { ...filterData, page: page.selected, size: ItemsPerPage }, { loader: true })
		setFilterData({ ...filterData, page: page.selected })
		setPageCount(Math.ceil(response.headers['x-total-count'] / ItemsPerPage))
		setData(response.data)
	}

	async function getPoses() {
		const response = await GET('/services/web/api/pos-helper')
		if (response) {
			response.unshift({ id: null, name: t('ALL') })
			setPoses(response)
		}
	}

	// Sync page-by-page until Didox returns no next page (mirrors SyncIncomingInvoices).
	async function sync() {
		if (!token) {
			toast.error(t('DIDOX_TOKEN_REQUIRED'))
			return
		}
		if (!filterData.posId) {
			toast.error(t('POS') + ': ' + t('REQUIRED_FIELD'))
			return
		}
		sessionStorage.setItem('didoxToken', token)
		setSyncing(true)
		const totals = { created: 0, updated: 0, skipped: 0 }
		let page = 1
		let total = null
		try {
			do {
				const res = await POST('/services/web/api/didox/sync-incoming-invoices',
					{ token, posId: filterData.posId, from: syncFrom, to: syncTo, page })
				const body = res?.data
				if (!body) break
				totals.created += body.created || 0
				totals.updated += body.updated || 0
				totals.skipped += body.skipped || 0
				total = body.total
				setSyncProgress({ ...totals, page, total })
				page = body.nextPage
			} while (page)
			toast.success(`${t('CREATED')}: ${totals.created} · ${t('UPDATED')}: ${totals.updated} · ${t('SKIPPED')}: ${totals.skipped}`)
			getData()
		} catch (e) {
			toast.error(t('ERROR'))
		} finally {
			setSyncing(false)
		}
	}

	// Fetch the pre-filled documents_in payload and open the reception modal in-place.
	async function importToDocumentIn(item) {
		const prefill = await GET(
			`/services/web/api/didox/incoming-invoices/${item.id}/document-in-prefill`,
			{ posId: filterData.posId, currencyId: 1, paymentTypeId: 1 },
			{ loader: true }
		)
		if (prefill) {
			setPrefillData({ ...prefill, incomingInvoiceId: prefill.incomingInvoiceId || item.id })
			setShowReception(true)
		}
	}

	function statusBadge(status) {
		// invoice_status: 0 waiting, 1 signed, 2 deleted, 3 denied, 100 unknown
		if (status === 1) return <span className="bg-success bg-border text-white">{t('SIGNED')}</span>
		if (status === 0) return <span className="bg-warning bg-border text-white">{t('WAITING')}</span>
		if (status === 2) return <span className="bg-secondary bg-border text-white">{t('DELETED')}</span>
		if (status === 3) return <span className="bg-danger bg-border text-white">{t('DENIED')}</span>
		return <span className="bg-secondary bg-border text-white">-</span>
	}

	useEffect(() => {
		getData()
		getPoses()
	}, [ItemsPerPage, filterData.random]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between mb-2">
				<h4 className="vertical-center">{t('DIDOX_INCOMING_INVOICES')}</h4>
			</div>

			{/* Sync panel */}
			<div className="card mb-3">
				<div className="card-body">
					<div className="row g-2 align-items-end">
						<div className="form-group col-md-3">
							<label>{t('POS')}</label>
							<Select
								options={poses}
								value={poses.find(o => o.id === filterData.posId) || ''}
								onChange={(o) => setFilterData({ ...filterData, posId: o.id })}
								placeholder=""
								noOptionsMessage={() => t('LIST_IS_EMPTY')}
								getOptionLabel={(o) => o.name}
								getOptionValue={(o) => o.id}
							/>
						</div>
						<div className="form-group col-md-2">
							<label>{t('FROM')}</label>
							<input type="date" className="form-control" value={syncFrom} onChange={(e) => setSyncFrom(e.target.value)} />
						</div>
						<div className="form-group col-md-2">
							<label>{t('TO')}</label>
							<input type="date" className="form-control" value={syncTo} onChange={(e) => setSyncTo(e.target.value)} />
						</div>
						<div className="form-group col-md-3">
							<label>{t('DIDOX_LOGIN')}</label>
							<button type="button"
								className={`btn w-100 ${token ? 'btn-outline-success' : 'btn-outline-primary'}`}
								onClick={() => setShowLogin(true)}>
								<i className="uil uil-key-skeleton me-2"></i>
								{token ? t('CONNECTED') : t('DIDOX_LOGIN')}
							</button>
						</div>
						<div className="form-group col-md-2">
							<button type="button" className="btn btn-primary w-100" onClick={sync} disabled={syncing}>
								{syncing
									? <><span className="spinner-border spinner-border-sm me-2"></span>{t('SYNCING')}</>
									: <><i className="uil uil-sync me-2"></i>{t('SYNC_FROM_DIDOX')}</>}
							</button>
						</div>
					</div>
					{syncProgress &&
						<div className="mt-2 text-muted">
							{t('PROCESSED')}: {syncProgress.page * 10}{syncProgress.total ? ` / ${syncProgress.total}` : ''} ·{' '}
							{t('CREATED')}: {syncProgress.created} · {t('UPDATED')}: {syncProgress.updated} · {t('SKIPPED')}: {syncProgress.skipped}
						</div>
					}
				</div>
			</div>

			{/* List */}
			<div className="card">
				<div className="card-body">
					<div className="d-flex">
						<Dropdown onToggle={() => setDropdownIsOpen(!dropdownIsOpen)} show={dropdownIsOpen}>
							<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100 me-2">
								{t('FILTER')}
							</Dropdown.Toggle>
							<Dropdown.Menu className="dropdown-filter">
								<div className="row">
									<div className="form-group col-md-12">
										<label>{t('STATUS')}</label>
										<Select
											options={statuses}
											value={statuses.find(o => o.id === filterData.status) || statuses[0]}
											onChange={(o) => setFilterData({ ...filterData, status: o.id })}
											getOptionLabel={(o) => o.name}
											getOptionValue={(o) => o.id}
										/>
									</div>
								</div>
								<div className="row mt-3">
									<div className="col-md-12 d-flex justify-content-end">
										<button type="button" className="btn btn-secondary me-2" onClick={() => setDropdownIsOpen(false)}>{t('CANCEL')}</button>
										<button type="button" className="btn btn-primary" onClick={() => setFilterData({ ...filterData, page: 0, random: filterData.random + 1 })}>
											{t('APPLY')}
										</button>
									</div>
								</div>
							</Dropdown.Menu>
						</Dropdown>
					</div>

					<ItemPerPage itemsPerPage={ItemsPerPage} setItemsPerPage={(e) => setItemsPerPage(e)} />

					<div className="table-responsive">
						<table className="table table-striped mb-0">
							<thead>
								<tr>
									<th>{t('SUPPLIER')}</th>
									<th className="text-center">№ {t('DOCUMENT')}</th>
									<th className="text-center">{t('DATE')}</th>
									<th className="text-center">{t('AMOUNT')}</th>
									<th className="text-center">{t('STATUS')}</th>
									<th className="text-center">{t('IMPORT')}</th>
									<th className="text-center">{t('ACTION')}</th>
								</tr>
							</thead>
							<tbody>
								{data.length > 0 ? data.map((item, index) => (
									<tr key={index}>
										<td>
											{item.rowNum}. {item.senderName}
											<div className="text-muted">{t('TIN')}: {item.senderTin}</div>
										</td>
										<td className="text-center">{item.invoiceNumber}</td>
										<td className="text-center">{formatDateWithTime(item.createdDate)}</td>
										<td className="text-center">{formatMoney(item.summa)}</td>
										<td className="text-center">{statusBadge(item.invoiceStatus)}</td>
										<td className="text-center">
											{item.imported
												? <span className="bg-success bg-border text-white">{t('IMPORTED')}</span>
												: <span className="bg-light bg-border">{t('NOT_IMPORTED')}</span>}
										</td>
										<td className="text-center">
											{canImport &&
												<button type="button" className="btn btn-sm btn-primary"
													disabled={item.imported || item.invoiceStatus !== 1}
													onClick={() => importToDocumentIn(item)}>
													<i className="uil uil-import me-1"></i>{t('IMPORT_TO_DOCUMENT_IN')}
												</button>
											}
										</td>
									</tr>
								)) : <tr><td colSpan="7" className="text-center">{t('NO_DATA')}</td></tr>}
							</tbody>
						</table>
					</div>

					<Pagination pageCount={pageCount} paginate={paginate} reduxPage={filterData.page}></Pagination>
				</div>
			</div>

			<DidoxLoginModal
				show={showLogin}
				onClose={() => setShowLogin(false)}
				onSuccess={(tk) => { setToken(tk); sessionStorage.setItem('didoxToken', tk) }}
			/>

			{showReception &&
				<CreateUpdateModal
					didoxPrefill={prefillData}
					callbakc_func={(s) => { setShowReception(s); getData() }}
				/>
			}
		</>
	)
}

export default Index
